import LOCATIONS from '@/constants/locations'
import { useGameplayContext } from '@/contexts/gameplayContext'
import {
  CurrencyEnum,
  GamePlayData,
  JobEnum,
  LocationEnum,
  Quest as QuestType,
  SkillEnum,
  UnlockRule,
  UnlockRuleCategory
} from '@/types'
import { checkUnlockRules, formatCurrency, formatUnderscoreCase, isUnlockRuleMet } from '@/utils'
import {
  Award,
  Briefcase,
  DollarSign,
  Globe,
  MapPin,
  Trophy,
  Waves,
  CheckCircle,
  ShoppingBag,
  Bot,
  ArrowBigRightDash
} from 'lucide-react'
import { SkillPill } from './SkillPill'
import { IndividualSkillCard } from './IndividualSkillCard'
import { Link } from 'react-router-dom'
import CURRENCIES from '@/constants/currencies'

export const Quest = ({ quest, showTravelToLocation = true }: { quest: QuestType; showTravelToLocation?: boolean }) => {
  const { gameData, completeQuest } = useGameplayContext()
  const isMainQuest = quest?.type === 'main'
  const bgColor = isMainQuest ? 'bg-purple-100' : 'bg-orange-100'
  const badgeColor = isMainQuest ? 'bg-purple-500' : 'bg-orange-500'

  const canComplete = checkUnlockRules(quest?.unlockRequirements || [], gameData)
  const isCompleted = gameData.completedQuests.includes(quest?.key || '')

  const handleComplete = () => {
    if (canComplete && !isCompleted) {
      completeQuest(quest.key)
    }
  }

  const getRequirementText = (req: UnlockRule, gameData: GamePlayData, location?: LocationEnum) => {
    switch (req.category) {
      case 'job':
        const jobHours = gameData.jobs[req.key as JobEnum] || 0
        return `Work at least ${req.value} hours as a ${formatUnderscoreCase(req.key)} (${formatCurrency(jobHours)})`

      case 'local_stat':
        if (req.key === 'wavesTotal') {
          return `Catch at least ${req.value} waves (${formatCurrency(
            gameData.locationStats[location || gameData.currentLocation].wavesTotal || 0
          )})`
        } else if (req.key === 'respectTotal') {
          return `Earn ${req.value} respect points (${formatCurrency(
            gameData.locationStats[location || gameData.currentLocation].respectTotal || 0
          )})`
        } else if (req.key === 'gameDaysTotal') {
          return `Surf for at least ${req.value} days (${Math.floor(
            gameData.locationStats[location || gameData.currentLocation].gameDaysTotal || 0
          )})`
        } else if (req.key === 'wipeoutsTotal') {
          return `Wipe out at least ${req.value} times (${Math.floor(
            gameData.locationStats[location || gameData.currentLocation].wipeoutsTotal || 0
          )})`
        } else if (req.key === 'criticalMovesTotal') {
          return `Perform at least ${req.value} critical moves (${Math.floor(
            gameData.locationStats[location || gameData.currentLocation].criticalMovesTotal || 0
          )})`
        } else if (req.key === 'surfSessionsTotal') {
          return `Surf at least ${req.value} sessions (${Math.floor(
            gameData.locationStats[location || gameData.currentLocation].surfSessionsTotal || 0
          )})`
        } else if (req.key === 'injuriesTotal') {
          return `Get injured at least ${req.value} ${req.value === 1 ? 'time' : 'times'} (${Math.floor(
            gameData.locationStats[location || gameData.currentLocation].injuriesTotal || 0
          )})`
        }
        return `${req.key}: ${req.value}`

      case 'currency':
        return `Save $${formatCurrency(req.value as number)} ($${formatCurrency(gameData.currencies.money.amount)})`

      case 'purchase':
        return `Purchase ${formatUnderscoreCase(req.key)}`
      case 'level':
        return `Reach Level ${req.value} in ${formatUnderscoreCase(req.key)}`
      case 'contest':
        if (req.value === 'won') {
          return `Win ${formatUnderscoreCase(req.key)}`
        } else {
          return `Finish in ${formatUnderscoreCase(req.value as string)} or better at ${formatUnderscoreCase(req.key)}`
        }

      default:
        return `${req.key}: ${req.value}`
    }
  }

  const getRequirementIcon = (category: UnlockRuleCategory) => {
    switch (category) {
      case 'job':
        return <Briefcase className="w-4 h-4 mr-2" />
      case 'local_stat':
        return <Waves className="w-4 h-4 mr-2" />
      case 'currency':
        return <DollarSign className="w-4 h-4 mr-2" />
      case 'level':
        return <Award className="w-4 h-4 mr-2" />
      case 'purchase':
        return <ShoppingBag className="w-4 h-4 mr-2" />
      case 'contest':
        return <Trophy className="w-4 h-4 mr-2" />
      default:
        return <Award className="w-4 h-4 mr-2" />
    }
  }

  if (!quest) return null

  return (
    <div className={`${bgColor} rounded-lg shadow-md p-4 mb-4`}>
      <div className="flex justify-between items-start mb-2">
        <div>
          <span className={`${badgeColor} text-white text-xs font-bold mr-2 px-2.5 py-0.5 rounded`}>
            {isMainQuest ? 'MAIN' : 'SIDE'}
          </span>
          <h3 className="text-lg font-semibold inline-block">
            {quest.chapterIndex}.{quest.locationIndex} {quest.name}
          </h3>
        </div>
      </div>
      <blockquote className="border-l-4 border-gray-300 pl-4 my-4 italic">"{quest.story}"</blockquote>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          className={`${
            isMainQuest ? 'bg-purple-50 border-purple-200' : 'bg-orange-50 border-orange-200'
          } p-3 rounded-lg border`}
        >
          <h4 className="font-semibold ">Requirements:</h4>
          <span className="text-xs text-gray-700 mb-2">
            In location: <b>{LOCATIONS[quest.location].name}</b>
          </span>
          <ul className="space-y-2">
            {quest.unlockRequirements.map((req, index) => (
              <li
                key={index}
                className={`flex items-center text-sm ${
                  isUnlockRuleMet(req, gameData, quest.location) ? 'text-green-700' : 'text-yellow-700'
                }`}
              >
                {getRequirementIcon(req.category)}
                <span className={isUnlockRuleMet(req, gameData, quest.location) ? 'line-through ' : ''}>
                  {getRequirementText(req, gameData, quest.location)}
                </span>
              </li>
            ))}
          </ul>
          {quest.location === gameData.currentLocation ? (
            <>
              {!isCompleted && canComplete && (
                <button
                  onClick={handleComplete}
                  className={`flex items-center mt-4 px-4 py-2 rounded-md font-medium text-sm bg-green-500 text-white hover:bg-green-600`}
                >
                  <CheckCircle className="w-4 h-4 mr-1" /> Complete Quest
                </button>
              )}
            </>
          ) : (
            showTravelToLocation && (
              <div className="flex justify-start">
                <Link
                  to="/locations"
                  className="flex items-center mt-4 px-4 py-2 rounded-md font-medium text-sm bg-blue-500 text-white hover:bg-blue-600"
                >
                  <MapPin className="w-4 h-4 mr-1" /> Travel to {LOCATIONS[quest.location].name}
                </Link>
              </div>
            )
          )}
        </div>
        <div
          className={`${
            isMainQuest ? 'bg-purple-50 border-purple-200' : 'bg-orange-50 border-orange-200'
          } p-3 rounded-lg border`}
        >
          <h4 className="font-semibold mb-2">Rewards:</h4>
          <div className="space-y-2">
            {quest.effect.surfSpotUnlocked && (
              <span className="bg-teal-400 text-white text-xs font-medium mr-2 px-2.5 py-1 rounded-full flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                {quest.effect.surfSpotUnlocked}
              </span>
            )}
            {quest.effect.locationUnlocked && (
              <span className="bg-teal-600 text-white text-xs font-medium mr-2 px-2.5 py-1 rounded-full flex items-center">
                <Globe className="w-4 h-4 mr-1" />
                {LOCATIONS[quest.effect.locationUnlocked].name}
              </span>
            )}
            {quest.effect.automationUnlocked && (
              <span className="bg-indigo-500 text-white text-xs font-medium mr-2 px-2.5 py-1 rounded-full flex items-center">
                <Bot className="w-4 h-4 mr-1" />
                {formatUnderscoreCase(quest.effect.automationUnlocked)} Automation
              </span>
            )}
            {quest.effect.stokeCapUnlocked && (
              <span className="bg-yellow-500 text-white text-xs font-medium mr-2 px-2.5 py-1 rounded-full flex items-center">
                🤙
                {formatCurrency(quest.effect.stokeCapUnlocked)} max Stoke
              </span>
            )}
            {quest.effect.gameSpeedUnlocked && (
              <span className="bg-yellow-500 text-white text-xs font-medium mr-2 px-2.5 py-1 rounded-full flex items-center">
                <ArrowBigRightDash className="w-4 h-4 mr-1" />x{formatCurrency(quest.effect.gameSpeedUnlocked)} max game
                speed
              </span>
            )}
            {quest.effect.multipliers &&
              quest.effect.multipliers.map((multiplier, index) => (
                <SkillPill
                  key={index}
                  skill={multiplier.key as SkillEnum}
                  value={multiplier.value}
                  Icon={
                    multiplier.effectCategory === 'currency'
                      ? CURRENCIES[multiplier.key as CurrencyEnum].icon
                      : undefined
                  }
                />
              ))}
            {quest.effect.skillsUnlocked && (
              <div className="space-y-2">
                {quest.effect.skillsUnlocked.map((skillKey) => (
                  <IndividualSkillCard
                    key={skillKey}
                    skillKey={skillKey}
                    reachedLevel={1}
                    requiredLevel={1}
                    gameSkill={gameData.skills[skillKey] || { points: 0, level: 0 }}
                    showDetails={false}
                    showLevel={false}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
